import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

export type StatusBadgeSeverity =
  | "critical"
  | "high"
  | "medium"
  | "warning"
  | "low"
  | "info"
  | "active"
  | "new"
  | "resolved"
  | "coming-soon";

interface StatusBadgeProps {
  severity: StatusBadgeSeverity;
  label: string;
  compact?: boolean;
}

function getSeverityColor(theme: Theme, severity: StatusBadgeSeverity): string {
  switch (severity) {
    case "critical":
      return theme.palette.error.main;
    case "high":
    case "medium":
    case "warning":
      return theme.palette.warning.main;
    case "low":
    case "info":
      return theme.palette.info.main;
    case "active":
      return theme.palette.error.main;
    case "resolved":
      return theme.palette.success.main;
    case "coming-soon":
      return theme.palette.primary.main;
    default:
      return theme.palette.text.secondary;
  }
}

/**
 * Returns the legible-text variant for a status badge: brighter than
 * `getSeverityColor`'s fill/border tone so labels stay above 4.5:1 contrast when
 * rendered on top of their own alpha-tinted chip background.
 */
function getSeverityTextColor(theme: Theme, severity: StatusBadgeSeverity): string {
  switch (severity) {
    case "critical":
      return theme.palette.error.light;
    case "high":
    case "medium":
    case "warning":
      return theme.palette.warning.light;
    case "low":
    case "info":
      return theme.palette.info.light;
    case "active":
      return theme.palette.error.light;
    case "resolved":
      return theme.palette.success.light;
    case "coming-soon":
      return theme.palette.primary.light;
    default:
      return theme.palette.text.secondary;
  }
}

/**
 * Compact severity or status pill for notification lists, alerts, and state indicators.
 * Color is always paired with a text label so color is never the sole signal.
 */
export function StatusBadge({ severity, label, compact = false }: StatusBadgeProps) {
  return (
    <Chip
      label={label}
      size="small"
      sx={(theme) => {
        const color = getSeverityColor(theme, severity);
        const textColor = getSeverityTextColor(theme, severity);
        return {
          height: compact ? 18 : 20,
          fontSize: compact ? "0.5625rem" : "0.625rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          borderRadius: "4px",
          backgroundColor: alpha(color, 0.15),
          color: textColor,
          border: `1px solid ${alpha(color, 0.35)}`,
          "& .MuiChip-label": { px: compact ? 0.75 : 1 },
        };
      }}
    />
  );
}
